import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { StoreGroupsApi } from "apis/StoreGroupsApi";
import { StoreGroupsTreeFilterQuery } from "models/filters/storeGroups/StoreGroupsTreeFilterQuery";
import { StoreGroupStoresApi } from "apis/StoreGroupStoresApi";
import { StoreGroup, StoreGroupView } from "models/dbModels/storeGroups/StoreGroup";
import { PaginationSelectionModel } from "models/common/paginationSelectionModel";
import { StoreGroupStore } from "models/dbModels/storeGroups/StoreGroupStores";
import { StoresApi } from "apis/StoresApi";
import { StoreGroupTree } from "models/dbModels/other/StoreGroupTree";
import { PaginatedResponse } from "models/common/responseObjects";
import { AxiosResponse } from "axios";

export const getStoreGroupsTable = createAsyncThunk("storeGroupsSlice/getStoreGroups", async () => {
    const response = await StoreGroupsApi.Search({});
    return response.data;
});

export const getStoreGroupsTree = createAsyncThunk("storeGroupsSlice/getStoreGroupsTree", async (params: StoreGroupsTreeFilterQuery) => {
    const response = await StoreGroupsApi.SearchTree(params);
    return response.data;
});

export const getStoreGroupsById = createAsyncThunk("storeGroupsSlice/getStoreGroupsById", async (id: number) => {
    const response = await StoreGroupsApi.Search({ storeGroupIds: [id] });
    return response.data;
});

export const getStoreGroupsStoresById = createAsyncThunk("storeGroupsSlice/getStoreGroupsStoresById", async (ids: number[]) => {
    const response = await StoreGroupStoresApi.Search({ storeGroupIds: ids });
    return response.data;
});

export const getStoreGroupsStoresByKeyWord = createAsyncThunk("storeGroupsSlice/getStoreGroupsStoresById", async (keyword: string | null) => {
    let response: AxiosResponse<PaginatedResponse<StoreGroupStore[]>> | undefined = undefined;
    
    if(keyword == null){
        response = await StoreGroupStoresApi.Search({});
    } else {
        response = await StoreGroupStoresApi.Search({ keyword: keyword! });
    }
    return response.data;
});

export const createStoreGroup = createAsyncThunk("storeGroupsSlice/createStoreGroup", async (data: StoreGroup) => {
    data = { ...data, id: 0 };
    const response = await StoreGroupsApi.Add([data]);
    return response.data;
});

export const updateStoreGroup = createAsyncThunk("storeGroupsSlice/updateStoreGroup", async (data: StoreGroup) => {
    const response = await StoreGroupsApi.Update([data]);
    return response.data;
});

export const deleteStoreGroup = createAsyncThunk("storeGroupsSlice/deleteStoreGroup", async (data: PaginationSelectionModel<number>) => {
    const response = await StoreGroupsApi.Delete(data);
    return response.data;
});

export const updateStoreGroupStores = createAsyncThunk("storeGroupsSlice/updateStoreGroupStores", async (data: StoreGroupStore[]) => {
    const response = await StoreGroupStoresApi.Update(data);
    return response.data;
});

export const deleteStoresByGroup = createAsyncThunk("storeGroupsSlice/deleteStoresByGroup", async (ids: number[]) => {
    const response = await StoreGroupStoresApi.DeleteByGroup(ids);
    return response.data;
});

export const getStoreGroupStoreTable = createAsyncThunk("storeGroupsSlice/getStoreGroupStoreTable", async (id: number) => {
    const response = await StoresApi.Search({ storeGroupIds: [id] });
    return response.data;
});

type InitialStateType = {
    storegroup: StoreGroupView;
    stores: number[];
    storesTable: any[];
    fullStoresList: any[];
    loading: boolean;
    table: any[];
    totalRows: number;
}

const initialState : InitialStateType = {
    storegroup: {
        id: 0,
        name: "",
        ownerId: 0,
        parentStoreGroupId: 1,
        storeCount: 0,
        useRootGroup: true,
        comments: undefined
    },
    stores: [],
    storesTable: [],
    fullStoresList: [],
    loading: false,
    table: [],
    totalRows: 0
}

const storeGroupsSlice = createSlice({
    name: "storeGroupsSlice",
    initialState: {
        ...initialState,
    },
    reducers: {
        cleanStoreGroupDetails: (state) => {
            state.storegroup = {
                id: 0,
                name: "",
                ownerId: 0,
                parentStoreGroupId: 1,
                storeCount: 0,
                useRootGroup: true,
                comments: undefined
            }
            state.stores = [];
            state.storesTable = [];
            state.loading = false;
        },
        updateField: (state, action) => {
            return {
                ...state,
                [action.payload.key]: action.payload.value,
            };
        },
        updateStoreGroupField: (state, action) => {
            return {
                ...state,
                storegroup: {
                    ...state.storegroup,
                    [action.payload.key]: action.payload.value,
                }
            };
        },
        setStoreGrupStores: (state, action) => {
            // @ts-ignore
            state.stores = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getStoreGroupsTable.fulfilled, (state, action) => {
            state.table = action.payload.data ? action.payload.data : [];
            state.totalRows = action.payload.data ? action.payload.data.length : 0;
        });
        builder.addCase(getStoreGroupsTree.fulfilled, (state, action) => {
            state.table = action.payload.data ? action.payload.data : [];
            state.totalRows = action.payload.data ? action.payload.data.length : 0;
        });
        builder.addCase(getStoreGroupsById.pending, (state) => {
            return {
                ...state,
                ...initialState,
            }
        });
        builder.addCase(getStoreGroupsById.fulfilled, (state, action) => {
            const storeGroupId = action.meta.arg;
            action.payload.data.forEach((store) => {
                if (store.id === storeGroupId) {
                    state.storegroup = store;
                }
            });
        });
        builder.addCase(getStoreGroupsStoresById.pending, (state) => {
            state.loading = true;
            state.stores = [];  
            state.storesTable = [];
        });
        builder.addCase(getStoreGroupsStoresById.fulfilled, (state, action) => {
            const storeGroupId = action.meta.arg[0];
            let stores: any[] = [];
            action.payload.data.forEach((store: any) => {
                if (store.storeGroupId === storeGroupId) stores.push(store.storeId);
            });
            state.stores = stores as never;
        });
        builder.addCase(getStoreGroupStoreTable.pending, (state) => {
            state.loading = true;
            state.storesTable = [];
            state.stores = [];
        });
        builder.addCase(getStoreGroupStoreTable.fulfilled, (state, action) => {
            state.stores = action.payload.data.map((store: any) => store.id);
            state.storesTable = action.payload.data;
            state.loading = false;
            state.totalRows = action.payload.data ? action.payload.data.length : 0;
        });
    }
});

export const { updateField, updateStoreGroupField, cleanStoreGroupDetails, setStoreGrupStores } = storeGroupsSlice.actions;

export default storeGroupsSlice.reducer;